import { useDispatch, useSelector } from 'react-redux';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import React, { useEffect, useState, useRef } from 'react';
import { TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';

import './SalesAccounts.css';
import EmailSettingsModal from '../../components/Integration/EmailSettingsModal';
import SignatureModal from '../../components/Signature/SignatureModal';
import TableAnimationFiles from '../../components/Animations/TableAnimationFiles';
import { getSalesAccounts, createSalesAccount, updateSalesAccount, deleteSalesAccount, getIntegrations, linkIntegrationToSalesAccount, unlinkIntegrationFromSalesAccount, getEmailSettings } from '../../store/Integration';

// Role options for the dropdown
const ROLE_OPTIONS = [
    'Chief Executive Officer',
    'Director',
    'Software Engineer',
    'Consultant',
    'Account Manager',
    'Engineer',
    'Professor',
    'Sales Manager',
    'Sales',
    'Partner',
    'Marketing Manager',
    'Business Development',
    'Customer Success',
    'Support'
];

// Integration type configurations
const INTEGRATION_CONFIG = {
    email: {
        name: 'Gmail',
        title: 'Gmail',
        toolName: 'Gmail',
        description: 'Connect your Gmail accounts',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="#EA4335"/>
            </svg>
        ),
    },
    sms: {
        toolName: 'Twilio',
        name: 'SMS (Twilio)',
        title: 'SMS (Twilio)',
        description: 'Connect your Twilio accounts',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2Z" fill="#F22F46"/>
            </svg>
        ),
    },
    whatsapp: {
        name: 'WhatsApp',
        title: 'WhatsApp',
        toolName: 'WhatsApp',
        description: 'Connect your WhatsApp accounts',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" fill="#25D366"/>
            </svg>
        ),
    }
}

const SalesAccounts = () => {
    const dispatch = useDispatch();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [formData, setFormData] = useState({ name: '', role: '' });

    // Collapsible state for each channel - all expanded by default
    const [expandedChannels, setExpandedChannels] = useState({ sms: true, email: true, whatsapp: true });

    // Connect integration popover state
    const popoverRef = useRef(null);
    const [connectPopover, setConnectPopover] = useState({ open: false, channel: null });

    // Email settings modal state
    const [emailSettingsOpen, setEmailSettingsOpen] = useState(false);
    const [selectedIntegrationForSettings, setSelectedIntegrationForSettings] = useState(null);

    // Signature modal state
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [savingSignature, setSavingSignature] = useState(false);

    const integrationState = useSelector(state => state['Integration'] || {});
    const { currentWorkspace } = useSelector(state => state['Settings'] || {});

    // Safe access to state with defaults
    const integrations = integrationState['integrations'] || [];
    const salesAccounts = integrationState['salesAccounts'] || { loading: false, data: [], error: null };
    const salesAccountForm = integrationState['salesAccountForm'] || { loading: false, success: false, error: null };
    const emailSettings = integrationState['emailSettings'] || { data: {} };

    const userRole = currentWorkspace?.['role'] || 'view';
    const canManage = ['owner', 'admin', 'editor'].includes(userRole);

    useEffect(() => {
        dispatch(getSalesAccounts());
        dispatch(getIntegrations());
    }, [dispatch]);

    // Auto-select first account when data loads
    useEffect(() => {
        if (!selectedAccount && salesAccounts['data'].length > 0)
            setSelectedAccount(salesAccounts['data'][0]);
    }, [salesAccounts['data']]);

    // Close modal on successful create
    useEffect(() => {
        if (salesAccountForm['success'] && showCreateModal) {
            setShowCreateModal(false);
            resetForm();
        }
    }, [salesAccountForm['success']]);

    // Update selectedAccount when salesAccounts data changes
    useEffect(() => {
        if (selectedAccount) {
            const updated = salesAccounts.data.find(acc => acc._id === selectedAccount._id);
            if (updated) setSelectedAccount(updated);
        }
    }, [salesAccounts.data]);

    // Fetch email settings for all linked email integrations
    useEffect(() => {
        if (selectedAccount?.integrations) {
            const emailIntegrations = selectedAccount.integrations.filter(i => i?.toolId?.name === 'Gmail');
            emailIntegrations.forEach(integration => {
                if (!emailSettings.data[integration._id]) dispatch(getEmailSettings(integration._id));
            });
        }
    }, [selectedAccount?.integrations, dispatch]);

    const resetForm = () => setFormData({ name: '', role: '' });

    const handleOpenCreate = () => {
        resetForm();
        setShowCreateModal(true);
    };

    const handleCloseCreateModal = () => {
        setShowCreateModal(false);
        resetForm();
    };

    const handleCreateSubmit = (e) => {
        e.preventDefault();
        dispatch(createSalesAccount({ name: formData.name, role: formData.role }));
    };

    const handleSelectAccount = (account) => setSelectedAccount(account);

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this sales account?')) {
            dispatch(deleteSalesAccount(id));
            setSelectedAccount(null);
        }
    };

    const handleUpdateRole = (newRole) => {
        if (!selectedAccount) return;
        dispatch(updateSalesAccount(selectedAccount._id, { role: newRole }));
    };

    const getIntegrationLabel = (integration) => {
        if (!integration) return '';
        return integration.tokens?.email || integration.accountIdentifier || integration.publicKey || integration.whatsappAccount?.phoneNumber || integration.name;
    };

    // Get integrations by channel type from the global integrations list
    const getAvailableIntegrationsByChannel = (channel) => {
        const toolName = INTEGRATION_CONFIG[channel]?.toolName;
        return integrations.filter(i => i?.toolId?.name === toolName);
    };

    // Get integrations linked to the current sales account by channel type
    const getLinkedIntegrationsByChannel = (channel) => {
        if (!selectedAccount?.integrations) return [];
        const toolName = INTEGRATION_CONFIG[channel]?.toolName;
        return selectedAccount.integrations.filter(i => i?.toolId?.name === toolName);
    };

    // Toggle channel expansion
    const toggleChannel = (channel) => {
        setExpandedChannels(prev => ({ ...prev, [channel]: !prev[channel] }));
    };

    // Handle opening the connect popover
    const handleOpenConnectPopover = (channel) => setConnectPopover({ open: true, channel });

    // Handle closing the connect popover
    const handleCloseConnectPopover = () => setConnectPopover({ open: false, channel: null });

    // Handle linking an integration to sales account
    const handleLinkIntegration = (integrationId) => {
        if (!selectedAccount) return;
        dispatch(linkIntegrationToSalesAccount(selectedAccount._id, integrationId));
        handleCloseConnectPopover();
    };

    // Handle unlinking an integration from sales account
    const handleUnlinkIntegration = (integrationId) => dispatch(unlinkIntegrationFromSalesAccount(integrationId));

    // Handle navigate to integrations page to add new
    const handleAddNewIntegration = () => window.location.href = '/connections/integrations';

    // Get email stats for an integration
    const getEmailStats = (integrationId) => {
        const settings = emailSettings.data[integrationId];
        if (!settings) return { warmupEmails: 0, emailsSent: 0, healthScore: 0 };
        const stats = settings.stats || {};
        // Calculate health score based on inbox rate, open rate, and reply rate
        const inboxRate = stats.inboxRate || 0;
        const openRate = stats.openRate || 0;
        const replyRate = stats.replyRate || 0;
        const healthScore = Math.round((inboxRate * 0.5) + (openRate * 0.3) + (replyRate * 0.2));
        return { warmupEmails: stats.received || 0, emailsSent: stats.sent || 0, healthScore };
    };

    // Get health score color
    const getHealthScoreColor = (score) => {
        if (score >= 80) return '#10B981';
        if (score >= 50) return '#F59E0B';
        return '#EF4444';
    };

    // Handle opening email settings modal
    const handleOpenEmailSettings = (integration) => {
        setEmailSettingsOpen(true);
        setSelectedIntegrationForSettings(integration);
    };

    // Handle closing email settings modal
    const handleCloseEmailSettings = () => {
        setEmailSettingsOpen(false);
        setSelectedIntegrationForSettings(null);
    };

    // Handle opening signature modal
    const handleOpenSignatureModal = () => setSignatureModalOpen(true);

    // Handle closing signature modal
    const handleCloseSignatureModal = () => setSignatureModalOpen(false);

    // Handle saving signature
    const handleSaveSignature = (signatureContent) => {
        if (!selectedAccount) return;
        setSavingSignature(true);
        dispatch(updateSalesAccount(selectedAccount._id, {
            signature: signatureContent,
            onSuccess: () => { setSavingSignature(false); handleCloseSignatureModal(); },
            onFailure: () => setSavingSignature(false)
        }));
    };

    // Close popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef['current'] && !popoverRef['current'].contains(event['target'])) handleCloseConnectPopover();
        };

        if (connectPopover['open']) document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [connectPopover['open']]);

    if (salesAccounts['loading']) {
        return (
            <div className='integrations-tab sales-accounts-page'>
                <div className='sa-loading-container'>
                    <TableAnimationFiles />
                </div>
            </div>
        )
    }

    return (
        <div className='integrations-tab sales-accounts-page'>
            <div className='sa-main-layout'>
                {/* Left Sidebar - Sales Accounts List */}
                <div className='sa-sidebar'>
                    <h3 className='sa-sidebar-title'>Sales Accounts</h3>

                    <div className='sa-accounts-list'>
                        {salesAccounts.data.map(account => (
                            <div
                                key={account._id}
                                onClick={() => handleSelectAccount(account)}
                                className={`sa-account-item ${selectedAccount?._id === account._id ? 'active' : ''}`}
                            >
                                <div className='sa-account-avatar'>
                                    {account.name?.charAt(0)?.toUpperCase() || 'S'}
                                </div>
                                <span className='sa-account-name'>{account.name}</span>
                            </div>
                        ))}
                    </div>

                    {canManage && (
                        <div onClick={handleOpenCreate} className='sa-add-account-btn'>
                            <span>+</span> Add Sales Account
                        </div>
                    )}
                </div>

                {/* Right Content - Selected Sales Account Details */}
                <div className='sa-content'>
                    {selectedAccount ? (
                        <>
                            {/* Header */}
                            <div className='sa-header'>
                                <div className='sa-header-left'>
                                    <div className='sa-header-avatar'>
                                        <i className='fa fa-user'></i>
                                    </div>
                                    <div>
                                        <h2 className='sa-header-name'>{selectedAccount.name}</h2>
                                        <FormControl size="small" className='sa-role-select'>
                                            <Select
                                                value={selectedAccount.role || ''}
                                                onChange={(e) => handleUpdateRole(e.target.value)}
                                                displayEmpty
                                                variant="standard"
                                                className='sa-role-dropdown'
                                            >
                                                <MenuItem value="" className='sa-menu-placeholder'>Select a role...</MenuItem>
                                                {ROLE_OPTIONS.map(role => (
                                                    <MenuItem key={role} value={role}>{role}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                </div>
                                <div className='sa-header-actions'>
                                    {canManage && (
                                        <button className='sa-signature-btn' onClick={handleOpenSignatureModal}>
                                            <i className='fa fa-pencil'></i>
                                            {selectedAccount.signature ? 'Edit Signature' : 'Add Signature'}
                                        </button>
                                    )}
                                    {canManage && (
                                        <button className='btn-style-border sa-delete-btn' onClick={() => handleDelete(selectedAccount._id)}>
                                            <i className='fa fa-trash'></i>
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Integrations Section */}
                            <div className='sa-integrations-section'>
                                <h4 className='sa-integrations-title'>Integrations</h4>
                                <p className='sa-integrations-desc'>
                                    Integrate {selectedAccount.name} into your account to enhance your performance
                                </p>

                                <div className='sa-channels-list'>
                                    {['email', 'sms', 'whatsapp'].map(channel => {
                                        const config = INTEGRATION_CONFIG[channel];
                                        const linkedIntegrations = getLinkedIntegrationsByChannel(channel);
                                        const availableIntegrations = getAvailableIntegrationsByChannel(channel);
                                        const isPopoverOpen = connectPopover.open && connectPopover.channel === channel;
                                        const isExpanded = expandedChannels[channel];
                                        const hasLinkedIntegrations = linkedIntegrations.length > 0;

                                        // Get unlinked available integrations (not already linked to this sales account)
                                        const linkedIds = linkedIntegrations.map(i => i._id);
                                        const unlinkedIntegrations = availableIntegrations.filter(i => !linkedIds.includes(i._id));

                                        return (
                                            <div key={channel} className='sa-channel-card'>
                                                {/* Channel Header */}
                                                <div className='sa-channel-header'>
                                                    <div className='sa-channel-info'>
                                                        <div className={`sa-channel-icon ${channel}`}>
                                                            {config.icon}
                                                        </div>
                                                        <div>
                                                            <h5 className='sa-channel-title'>
                                                                {config.title}
                                                                {hasLinkedIntegrations && (
                                                                    <span className='sa-channel-count'>
                                                                        ({linkedIntegrations.length} connected)
                                                                    </span>
                                                                )}
                                                            </h5>
                                                            <p className='sa-channel-description'>
                                                                {config.description}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className='sa-channel-actions'>
                                                        {/* Connect button */}
                                                        <div className='sa-connect-wrapper'>
                                                            <button
                                                                onClick={() => handleOpenConnectPopover(channel)}
                                                                className='sa-connect-btn'
                                                            >
                                                                <i className='fa fa-plus'></i>
                                                                Connect
                                                            </button>

                                                            {/* Connect Popover */}
                                                            {isPopoverOpen && (
                                                                <div
                                                                    ref={popoverRef}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    className='sa-popover'
                                                                >
                                                                    <div className='sa-popover-header'>
                                                                        <p>Select {config.name} Account</p>
                                                                    </div>

                                                                    <div className='sa-popover-list'>
                                                                        {unlinkedIntegrations.length > 0 ? (
                                                                            unlinkedIntegrations.map(integration => (
                                                                                <div
                                                                                    key={integration._id}
                                                                                    onClick={() => handleLinkIntegration(integration._id)}
                                                                                    className='sa-popover-item'
                                                                                >
                                                                                    <span>{getIntegrationLabel(integration)}</span>
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            <div className='sa-popover-empty'>
                                                                                No available {config.name} accounts
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    <div className='sa-popover-footer'>
                                                                        <div
                                                                            onClick={handleAddNewIntegration}
                                                                            className='sa-popover-add'
                                                                        >
                                                                            <i className='fa fa-plus'></i>
                                                                            Add new {config.name} account
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Expand/Collapse chevron - only show if has linked integrations */}
                                                        {hasLinkedIntegrations && (
                                                            <button
                                                                onClick={() => toggleChannel(channel)}
                                                                className='sa-toggle-btn'
                                                            >
                                                                <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Linked Integrations List - only show when expanded */}
                                                {hasLinkedIntegrations && isExpanded && (
                                                    <div className='sa-linked-list'>
                                                        {/* Header row for email channel */}
                                                        {channel === 'email' && (
                                                            <div className='sa-linked-header'>
                                                                <span className='sa-linked-header-email'>Email</span>
                                                                <span className='sa-linked-header-stat'>Emails Sent</span>
                                                                <span className='sa-linked-header-stat'>Warmup Emails</span>
                                                                <span className='sa-linked-header-stat'>Health</span>
                                                                <span className='sa-linked-header-actions'>Actions</span>
                                                            </div>
                                                        )}
                                                        {linkedIntegrations.map((integration) => {
                                                            const stats = channel === 'email' ? getEmailStats(integration._id) : null;
                                                            return (
                                                                <div key={integration._id} className={`sa-linked-item ${channel === 'email' ? 'with-stats' : ''}`}>
                                                                    <div className='sa-linked-info'>
                                                                        <i className='fa fa-check-circle'></i>
                                                                        <span>{getIntegrationLabel(integration)}</span>
                                                                    </div>
                                                                    {/* Show stats for email integrations */}
                                                                    {channel === 'email' && stats && (
                                                                        <>
                                                                            <div className='sa-linked-stat'>
                                                                                <span className='sa-stat-value'>{stats.emailsSent}</span>
                                                                            </div>
                                                                            <div className='sa-linked-stat'>
                                                                                <span className='sa-stat-value'>{stats.warmupEmails}</span>
                                                                            </div>
                                                                            <div className='sa-linked-stat'>
                                                                                <div className='sa-health-badge' style={{ backgroundColor: getHealthScoreColor(stats.healthScore) + '20', color: getHealthScoreColor(stats.healthScore) }}>
                                                                                    {stats.healthScore}%
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                    <div className='sa-linked-actions'>
                                                                        {/* Settings button for Gmail only */}
                                                                        {channel === 'email' && canManage && (
                                                                            <button onClick={() => handleOpenEmailSettings(integration)} className='sa-settings-btn' title="Email Settings">
                                                                                <i className='fa fa-cog'></i>
                                                                            </button>
                                                                        )}
                                                                        {canManage && (
                                                                            <button onClick={() => handleUnlinkIntegration(integration._id)} className='sa-remove-btn'>
                                                                                <i className='fa fa-times'></i> Remove
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className='sa-empty-state'>
                            <div className='sa-empty-icon'>
                                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#C5C7CD"/>
                                </svg>
                            </div>
                            <h3 className='sa-empty-title'>No Sales Accounts Yet</h3>
                            <p className='sa-empty-desc'>Create your first sales account to start sending personalized outreach</p>
                            {canManage && (
                                <button className='btn-style-new' onClick={handleOpenCreate}>
                                    <i className='fa fa-plus'></i> Add Sales Account
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Create Sales Account Modal */}
            <Modal isOpen={showCreateModal} toggle={handleCloseCreateModal} size='md' backdrop="static">
                <ModalHeader toggle={handleCloseCreateModal}>
                    Create Sales Account
                </ModalHeader>
                <ModalBody>
                    <form onSubmit={handleCreateSubmit}>
                        <p className='sa-modal-desc'>New Sales Account</p>

                        <div className='sa-form-group'>
                            <TextField
                                fullWidth
                                label="Enter a name"
                                name="name"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Enter a name"
                                required
                                size="small"
                            />
                        </div>

                        <div className='sa-form-group'>
                            <FormControl fullWidth size="small">
                                <InputLabel>Select a role...</InputLabel>
                                <Select
                                    value={formData.role}
                                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                                    label="Select a role..."
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {ROLE_OPTIONS.map(role => (
                                        <MenuItem key={role} value={role}>{role}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>

                        <div className='sa-form-actions'>
                            <button type="button" className='btn-style-border' onClick={handleCloseCreateModal}>
                                Cancel
                            </button>
                            <button type="submit" className='btn-style-new' disabled={salesAccountForm.loading}>
                                {salesAccountForm.loading ? 'Creating...' : 'Create'}
                            </button>
                        </div>
                    </form>
                </ModalBody>
            </Modal>

            {/* Email Settings Modal */}
            <EmailSettingsModal isOpen={emailSettingsOpen} toggle={handleCloseEmailSettings} integration={selectedIntegrationForSettings} />

            {/* Signature Modal */}
            <SignatureModal
                isOpen={signatureModalOpen}
                toggle={handleCloseSignatureModal}
                signature={selectedAccount?.signature || ''}
                onSave={handleSaveSignature}
                saving={savingSignature}
            />
        </div>
    );
};

export default SalesAccounts;
